import PropTypes from 'prop-types'
import { Box, Button, ButtonGroup, Flex, FormControl, FormLabel, Grid, GridItem, IconButton, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Radio, RadioGroup, Stack, Text, Textarea, useDisclosure } from '@chakra-ui/react'
import { MdAttachment, MdCheckCircle, MdDeleteOutline, MdOutlineCheckCircle, MdOutlineEdit } from 'react-icons/md'
import { completeProjectSectionTask, deleteProjectInvoicingAttachmentByPath, updateProject, uploadProjectInvoicingAttachment } from 'api'
import { useAppContext } from 'hooks/useAppContext'
import { format as formatDate } from 'date-fns'
import { useState, useRef, useMemo } from 'react'
import { PROJECT_BALANCE_TERMS, PROJECT_DEPOSIT_STATUS, PROJECT_INVOICING_ACQUIRED_ACTIONS, PROJECT_INVOICING_CUSTOMER_DISPOSITION } from 'lib'
import { FILE_ATTACHMENT_STATUS, FileAttachment } from 'components/common'

export const ProjectSectionTaskRow = ({ task, onEditTask, onRemoveTask, reload, onOpenTaskNotes, showProjectName = false, showAssigneeName = true, ...rest }) => {
  const { createToast } = useAppContext()
  const disclosureClientVoltage = useDisclosure()
  const disclosureDepositOption = useDisclosure()
  const disclosureProjectToInvoicing = useDisclosure()

  const onCompleteTask = async () => {
    if (task.isCompleted) {
      handleCompleteTask()
    } else {
      switch (task.templateTaskUid) {
        case 'U-00073': case 'U-00074': // Confirm Client's Voltage
          disclosureClientVoltage.onOpen()
          break
        case 'U-00067': case 'U-00071': // PO / Deposit
          disclosureDepositOption.onOpen()
          break
        case 'U-00085': case 'U-00106': // Move Project to Invoicing
          disclosureProjectToInvoicing.onOpen()
          break
        default:
          handleCompleteTask()
          break
      }
    }
  }

  const handleCompleteTask = async () => {
    try {
      const isCompleted = !task.isCompleted
      await completeProjectSectionTask(task.id, isCompleted)
      createToast('success', 'Success', `Task marked as ${isCompleted ? 'complete' : 'incomplete'}.`)
      reload()
    } catch (err) {
      createToast('error', 'Error', 'Failed to update task completion status.')
    }
  }

  const handleOpenNote = () => {
    onOpenTaskNotes(task.id)
  }

  const dueDate = useMemo(() => {
    const date = new Date(task?.dueDate)
    return task?.dueDate ? formatDate(new Date(date.getTime() + date.getTimezoneOffset() * 60000), 'MM-dd-yyyy') : ''
  }, [task?.dueDate])

  return (
    <>
      <Grid
        key={task.id}
        templateColumns={showProjectName ? 'repeat(15, 1fr)' : 'repeat(11, 1fr)'}
        pl={8}
        pr={4}
        pt={2}
        pb="2px"
        gap={4}
        borderBottomWidth={1}
        borderBottomStyle="solid"
        borderBottomColor="gray.200"
        {...rest}
      >
        <GridItem colSpan={6}>
          <Flex gap={2} alignItems="center">
            <Box cursor="pointer" onClick={() => onCompleteTask(task.id, task.templateTaskUid, !task.isCompleted)}>
              {/* eslint-disable-next-line */}
              {task.isCompleted == true ? (
                <MdCheckCircle color="#48BB78" />
              ) : (
                <MdOutlineCheckCircle />
              )}
            </Box>
            <Text fontSize="sm" cursor="pointer" onClick={handleOpenNote}>{task.title}</Text>
          </Flex>
        </GridItem>
        {showAssigneeName && (
          <GridItem colSpan={2}>
            <Text fontSize="sm">{task.assignedUserName}</Text>
          </GridItem>
        )}
        <GridItem colSpan={2}>
          <Text fontSize="sm">{dueDate}</Text>
        </GridItem>
        {showProjectName && (
          <GridItem colSpan={4}>
            <Text fontSize="sm" cursor="pointer" color="blue.400" onClick={() => window.open(`/projects/detail/${task.projectId}`)}>{task.customerCompany}</Text>
          </GridItem>
        )}
        <GridItem colSpan={1} justifySelf="flex-end">
          <ButtonGroup>
            <IconButton
              variant="ghost"
              size="xs"
              colorScheme="gray"
              icon={<MdOutlineEdit fontSize="16px" />}
              onClick={() => onEditTask(task)}
            />
            <IconButton
              variant="ghost"
              size="xs"
              colorScheme="gray"
              icon={<MdDeleteOutline fontSize="16px" />}
              onClick={() => onRemoveTask(task)}
            />
          </ButtonGroup>
        </GridItem>
      </Grid>
      {disclosureClientVoltage.isOpen && (
        <ModalClientVoltage
          projectId={task.projectId}
          onSave={handleCompleteTask}
          {...disclosureClientVoltage}
        />
      )}
      {disclosureDepositOption.isOpen && (
        <ModalDepositOption
          projectId={task.projectId}
          onSave={handleCompleteTask}
          {...disclosureDepositOption}
        />
      )}
      {disclosureProjectToInvoicing.isOpen && (
        <ModalProjectToInvoicing
          projectId={task.projectId}
          onSave={handleCompleteTask}
          {...disclosureProjectToInvoicing}
        />
      )}
    </>
  )
}

ProjectSectionTaskRow.propTypes = {
  task: PropTypes.object,
  onEditTask: PropTypes.func,
  onRemoveTask: PropTypes.func,
  reload: PropTypes.func,
  onOpenTaskNotes: PropTypes.func,
  showProjectName: PropTypes.bool,
  showAssigneeName: PropTypes.bool
}

export const ModalClientVoltage = ({ projectId, isOpen, onClose, onSave }) => {
  const { createToast } = useAppContext()
  const [clientVoltage, setClientVoltage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSave = async (e) => {
    e.preventDefault()

    setIsSubmitting(true)
    try {
      await updateProject(projectId, { clientVoltage })
      createToast('success', 'Success', 'Project client voltage option has updated.')
      setIsSubmitting(false)
      onClose()
      onSave()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSave}>
          <ModalHeader>{'Client\'s Voltage'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>{'Select client\'s voltage option'}</FormLabel>
              <RadioGroup
                onChange={v => setClientVoltage(v)}
                value={clientVoltage}
              >
                <Stack direction="row" gap="8">
                  <Radio size="sm" value='120v'>120v</Radio>
                  <Radio size="sm" value='240v'>240v</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button type="submit" colorScheme="blue" mr={3} isLoading={isSubmitting}>
              Save
            </Button>
            <Button variant="ghost" onClick={onClose}>Close</Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

ModalClientVoltage.propTypes = {
  projectId: PropTypes.number,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func
}

export const ModalDepositOption = ({ projectId, isOpen, onClose, onSave }) => {
  const { createToast } = useAppContext()
  const [depositStatus, setDepositStatus] = useState('')
  const [balanceTerms, setBalanceTerms] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSave = async (e) => {
    e.preventDefault()

    setIsSubmitting(true)
    try {
      await updateProject(projectId, { depositStatus, balanceTerms })
      createToast('success', 'Success', 'Project deposit and balance terms options have updated.')
      setIsSubmitting(false)
      onClose()
      onSave()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSave}>
          <ModalHeader>Select Options</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>Select deposit status</FormLabel>
              <RadioGroup
                onChange={v => setDepositStatus(v)}
                value={depositStatus}
              >
                <Stack direction="row" gap="8">
                  <Radio size="sm" value={PROJECT_DEPOSIT_STATUS.POReceived}>{PROJECT_DEPOSIT_STATUS.POReceived}</Radio>
                  <Radio size="sm" value={PROJECT_DEPOSIT_STATUS.PaymentReceived}>{PROJECT_DEPOSIT_STATUS.PaymentReceived}</Radio>
                  <Radio size="sm" value={PROJECT_DEPOSIT_STATUS.POAndPaymentReceived}>{PROJECT_DEPOSIT_STATUS.POAndPaymentReceived}</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>
            <FormControl mt={4} isRequired>
              <FormLabel>Select balance terms</FormLabel>
              <RadioGroup
                onChange={v => setBalanceTerms(v)}
                value={balanceTerms}
              >
                <Stack direction="row" gap="8">
                  <Radio size="sm" value={PROJECT_BALANCE_TERMS.Shipping25Prior}>{PROJECT_BALANCE_TERMS.Shipping25Prior}</Radio>
                  <Radio size="sm" value={PROJECT_BALANCE_TERMS.Shipping100Prior}>{PROJECT_BALANCE_TERMS.Shipping100Prior}</Radio>
                  <Radio size="sm" value={PROJECT_BALANCE_TERMS.DueAfterInstall}>{PROJECT_BALANCE_TERMS.DueAfterInstall}</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button type="submit" colorScheme="blue" mr={3} isLoading={isSubmitting}>
              Save
            </Button>
            <Button variant="ghost" onClick={onClose}>Close</Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

ModalDepositOption.propTypes = {
  projectId: PropTypes.number,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func
}

export const ModalProjectToInvoicing = ({ projectId, isOpen, onClose, onSave }) => {
  const { createToast } = useAppContext()
  const [invoicingAcquiredAction, setInvoicingAcquiredAction] = useState('')
  const [invoicingCustomerDisposition, setInvoicingCustomerDisposition] = useState('')
  const [invoicingRemarks, setInvoicingRemarks] = useState('')
  const [attachments, setAttachments] = useState([])
  const [nextAttachmentId, setNextAttachmentId] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const fileInputRef = useRef(null)

  const handleFileChange = async (event) => {
    if (event.target.files && event.target.files.length) {
      const files = event.target.files
      // eslint-disable-next-line space-unary-ops
      for (let i = 0; i < files.length; i ++) {
        const file = files[i]
        const curAttachmentId = nextAttachmentId + i
        const attachment = {
          id: curAttachmentId,
          status: 'uploading',
          progress: 0
        }
        setAttachments(prevAttachments => [...prevAttachments, attachment])
        try {
          const response = await uploadProjectInvoicingAttachment({ file }, progressEvent => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            setAttachments(prevAttachments => prevAttachments.map(v => v.id === curAttachmentId ? { ...v, progress: percentCompleted } : v))
          })
          setAttachments(prevAttachments => prevAttachments.map(v => v.id === curAttachmentId ? { ...response, status: 'success' } : v))
        } catch (err) {
          setAttachments(prevAttachments => prevAttachments.map(v => v.id === curAttachmentId ? { ...v, status: 'fail' } : v))
        }
      }
      setNextAttachmentId(nextAttachmentId + files.length)
    }
  }

  const onAddAttachment = () => {
    fileInputRef.current.click()
  }

  const handleSave = async (e) => {
    e.preventDefault()

    setIsSubmitting(true)
    try {
      await updateProject(projectId, {
        invoicingAcquiredAction,
        invoicingCustomerDisposition,
        invoicingRemarks,
        invoicingAttachments: attachments.filter(v => v.status === FILE_ATTACHMENT_STATUS.Success).map(v => v.path).join(',')
      })
      createToast('success', 'Success', 'Project invoicing options have updated.')
      setIsSubmitting(false)
      onClose()
      onSave()
    } catch (err) {
      createToast('error', 'Error', err.response.data)
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSave}>
          <ModalHeader>Move Project to Invoicing</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>Invoicing Acquired Action</FormLabel>
              <RadioGroup
                onChange={v => setInvoicingAcquiredAction(v)}
                value={invoicingAcquiredAction}
              >
                <Stack direction="row" gap="8">
                  <Radio size="sm" value={PROJECT_INVOICING_ACQUIRED_ACTIONS.PowerOn}>{PROJECT_INVOICING_ACQUIRED_ACTIONS.PowerOn}</Radio>
                  <Radio size="sm" value={PROJECT_INVOICING_ACQUIRED_ACTIONS.SignOnline}>{PROJECT_INVOICING_ACQUIRED_ACTIONS.SignOnline}</Radio>
                  <Radio size="sm" value={PROJECT_INVOICING_ACQUIRED_ACTIONS.NotApplicable}>{PROJECT_INVOICING_ACQUIRED_ACTIONS.NotApplicable}</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>
            <FormControl mt={4} isRequired>
              <FormLabel>Invoicing Customer Disposition</FormLabel>
              <RadioGroup
                onChange={v => setInvoicingCustomerDisposition(v)}
                value={invoicingCustomerDisposition}
              >
                <Stack direction="row" gap="8">
                  <Radio size="sm" value={PROJECT_INVOICING_CUSTOMER_DISPOSITION.Displeased}>{PROJECT_INVOICING_CUSTOMER_DISPOSITION.Displeased}</Radio>
                  <Radio size="sm" value={PROJECT_INVOICING_CUSTOMER_DISPOSITION.Okay}>{PROJECT_INVOICING_CUSTOMER_DISPOSITION.Okay}</Radio>
                  <Radio size="sm" value={PROJECT_INVOICING_CUSTOMER_DISPOSITION.Happy}>{PROJECT_INVOICING_CUSTOMER_DISPOSITION.Happy}</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>
            <FormControl mt={4} isRequired>
              <FormLabel>Invoicing Remarks (max 140 characters)</FormLabel>
              <Textarea
                rows="4"
                value={invoicingRemarks}
                onChange={(e) => setInvoicingRemarks(e.target.value)}
                maxLength={140}
              />
            </FormControl>
            <Box my={8}>
              {attachments.length !== 0 && (
                <Flex gap={4} flexWrap="wrap" my={4}>
                  {attachments.map(attachment => {
                    return (
                      <FileAttachment
                        key={attachment.status === FILE_ATTACHMENT_STATUS.Success ? attachment.path : attachment.id}
                        canRemove={true}
                        id={attachment.id}
                        status={attachment.status}
                        progress={attachment.progress}
                        filename={attachment.filename || ''}
                        extension={attachment.extension || ''}
                        path={attachment.path || ''}
                        onRemove={async () => {
                          if (attachment.status === FILE_ATTACHMENT_STATUS.Success) {
                            try {
                              await deleteProjectInvoicingAttachmentByPath({ path: attachment.path })
                            } catch (err) {
                              console.log('Failed to remove temp file on the server.')
                            }
                          }
                          setAttachments(prevAttachments => prevAttachments.filter(v => {
                            return attachment.status === FILE_ATTACHMENT_STATUS.Success ? (v.path !== attachment.path) : (v.id !== attachment.id)
                          }))
                        }}
                      />
                    )
                  })}
                </Flex>
              )}
              <Flex justifyContent="space-between" my={2}>
                <Flex gap={2} cursor="pointer" alignItems="center" onClick={onAddAttachment}>
                  <IconButton
                    variant="ghost"
                    size="xs"
                    colorScheme="gray"
                    icon={<MdAttachment fontSize="18px" />}
                  />
                  <Text fontSize="xs">Add Attachment</Text>
                  <Input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileChange} multiple />
                </Flex>
              </Flex>
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button type="submit" colorScheme="blue" mr={3} isLoading={isSubmitting}>
              Save
            </Button>
            <Button variant="ghost" onClick={onClose}>Close</Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

ModalProjectToInvoicing.propTypes = {
  projectId: PropTypes.number,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func
}
